import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Bell,
  RefreshCw,
  Send,
  Check,
  AlertTriangle,
  Shield,
  DollarSign,
  Settings,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const formatAmount = (amount) => {
  return amount?.toLocaleString('ru-RU') || '0';
};

const NotificationSettingsPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [botInfo, setBotInfo] = useState(null);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const [settingsRes, botRes] = await Promise.all([
        api.get('/notification-settings'),
        api.get('/notification-settings/telegram-bot').catch(() => ({ data: null })),
      ]);
      setSettings(settingsRes.data);
      setBotInfo(botRes.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/notification-settings', {
        telegram_chat_id: settings.telegram_chat_id,
        telegram_enabled: settings.telegram_enabled,
        notify_aml_low: settings.notify_aml_low,
        notify_aml_medium: settings.notify_aml_medium,
        notify_aml_high: settings.notify_aml_high,
        notify_aml_critical: settings.notify_aml_critical,
        notify_large_payments: settings.notify_large_payments,
        large_payment_threshold: settings.large_payment_threshold,
        notify_system_alerts: settings.notify_system_alerts,
      });
      toast.success('Настройки сохранены');
    } catch (err) {
      toast.error('Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async () => {
    if (!settings.telegram_chat_id) {
      toast.error('Введите Telegram Chat ID');
      return;
    }
    
    try {
      setTesting(true);
      await api.post('/notification-settings/test', {
        channel: 'telegram',
        message: 'Тестовое уведомление работает! 🎉',
      });
      toast.success('Тестовое уведомление отправлено');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка отправки');
    } finally {
      setTesting(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notification-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('notifications.settings')}</h1>
          <p className="text-slate-400 mt-1">Настройка Telegram и email уведомлений</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-500 hover:bg-emerald-600"
          data-testid="save-settings-btn"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
          {t('common.save')}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Telegram Settings */}
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-400" />
              Telegram
            </CardTitle>
            <CardDescription className="text-slate-400">
              Получайте уведомления в Telegram мгновенно
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {botInfo && (
              <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <Badge className="bg-blue-500/20 text-blue-400">Бот активен</Badge>
                <a
                  href={`https://t.me/${botInfo.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 flex items-center gap-1 text-sm"
                >
                  @{botInfo.username}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-slate-300">Chat ID</Label>
              <Input
                value={settings?.telegram_chat_id || ''}
                onChange={(e) => updateSetting('telegram_chat_id', e.target.value)}
                placeholder="Ваш Telegram Chat ID"
                className="bg-slate-700/50 border-slate-600 text-white"
                data-testid="telegram-chat-id-input"
              />
              <p className="text-xs text-slate-500">
                Отправьте /start боту и используйте /myid чтобы узнать свой Chat ID
              </p>
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Включить уведомления</Label>
              <Switch
                checked={settings?.telegram_enabled || false}
                onCheckedChange={(checked) => updateSetting('telegram_enabled', checked)}
              />
            </div>

            <Button
              onClick={handleTestNotification}
              disabled={testing || !settings?.telegram_chat_id}
              variant="outline"
              className="w-full border-slate-600 text-slate-300"
              data-testid="test-notification-btn"
            >
              {testing ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Отправить тест
            </Button>
          </CardContent>
        </Card>

        {/* AML Alerts Settings */}
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-orange-400" />
              AML {t('aml.alerts')}
            </CardTitle>
            <CardDescription className="text-slate-400">
              Выберите уровни риска для уведомлений
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
              <div className="flex items-center gap-2">
                <Badge className="bg-red-500/20 text-red-400">Критический</Badge>
                <span className="text-slate-400 text-sm">Блокировка транзакции</span>
              </div>
              <Switch
                checked={settings?.notify_aml_critical || false}
                onCheckedChange={(checked) => updateSetting('notify_aml_critical', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
              <div className="flex items-center gap-2">
                <Badge className="bg-orange-500/20 text-orange-400">{t('aml.high')}</Badge>
                <span className="text-slate-400 text-sm">Требует проверки</span>
              </div>
              <Switch
                checked={settings?.notify_aml_high || false}
                onCheckedChange={(checked) => updateSetting('notify_aml_high', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
              <div className="flex items-center gap-2">
                <Badge className="bg-amber-500/20 text-amber-400">{t('aml.medium')}</Badge>
                <span className="text-slate-400 text-sm">Мониторинг</span>
              </div>
              <Switch
                checked={settings?.notify_aml_medium || false}
                onCheckedChange={(checked) => updateSetting('notify_aml_medium', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-500/20 text-emerald-400">{t('aml.low')}</Badge>
                <span className="text-slate-400 text-sm">Информация</span>
              </div>
              <Switch
                checked={settings?.notify_aml_low || false}
                onCheckedChange={(checked) => updateSetting('notify_aml_low', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Large Payments Settings */}
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              Крупные платежи
            </CardTitle>
            <CardDescription className="text-slate-400">
              Уведомления о платежах выше порога
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Уведомлять о крупных платежах</Label>
              <Switch
                checked={settings?.notify_large_payments || false}
                onCheckedChange={(checked) => updateSetting('notify_large_payments', checked)}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Порог суммы (KZT)</Label>
              <Input
                type="number"
                value={settings?.large_payment_threshold || 1000000}
                onChange={(e) => updateSetting('large_payment_threshold', parseFloat(e.target.value))}
                className="bg-slate-700/50 border-slate-600 text-white"
                data-testid="payment-threshold-input"
              />
              <p className="text-xs text-slate-500">
                Текущий порог: {formatAmount(settings?.large_payment_threshold)} ₸
              </p>
            </div>
          </CardContent>
        </Card>

        {/* System Alerts */}
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-slate-400" />
              Системные уведомления
            </CardTitle>
            <CardDescription className="text-slate-400">
              Важные системные события
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-300">Системные алерты</p>
                <p className="text-xs text-slate-500">Ошибки, обновления, важные события</p>
              </div>
              <Switch
                checked={settings?.notify_system_alerts || false}
                onCheckedChange={(checked) => updateSetting('notify_system_alerts', checked)}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NotificationSettingsPage;
